import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { editVulnerability as editVulnerabilityService } from 'services/modules/vapt/report';
import { fetchAlertById as fetchAlertByIdService } from 'services/modules/vapt/alert';

import { useNotification } from 'config/Notification';

import {
  Card,
  CardHeader,
  Divider,
  Button,
  CardContent,
  Checkbox,
  FormHelperText,
  FormControlLabel,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  TextField,
  IconButton,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/Upload';
import DeleteIcon from '@mui/icons-material/Delete';
import he from 'he';

import Editor from './Editor';


function EditVulnerability() {
  // Notification
  const { openNotification } = useNotification();
  // useParams hook
  const { id } = useParams();
  // Redux dispatch
  const dispatch = useDispatch();

  // Redux selectors
  const dimensionsContainer = useSelector((state) => state.vaptData.dimensions);
  const threatContainer = useSelector((state) => state.vaptAlert.threat);

  // State variables
  const severities = ["Informational", "Low", "Medium", "High", "Critical"];
  const [formErrors, setFormErrors] = useState({});
  const [scoreError, setScoreError] = useState('');
  const [validSeverities, setValidSeverities] = useState(severities);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    severity: '',
    score: '',
    impact: '',
    remediation: '',
    ThreatActor:'',
    Tactics:'',
    ThreatActorDescription:''
  });
  const [dynamicFields, setDynamicFields] = useState([]);
  const [checked, setChecked] = useState(false);
  const [evidence, setEvidence] = useState(threatContainer?.evidence)
  const [mainDimensionLabel, setMainDimensionLabel] = useState('');
  const [subDimensions, setSubDimensions] = useState([]);
  const [selectedSubDimension, setSelectedSubDimension] = useState(null);
  const [subDimensionLabel, setSubDimensionLabel] = useState("")

  // useEffect hook fetches alert by id and dimensions
  useEffect(() => {
    if (id !== null) {
      dispatch(fetchAlertByIdService({ id }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (threatContainer) {
      const dynamicFieldsData = threatContainer?.data ? Object.entries(threatContainer.data)
        .filter(([key]) => !['title', 'description', 'severity', 'score', 'impact', 'remediation', 'dimension','ThreatActor','Tactics','ThreatActorDescription'].includes(key))
        .map(([key, value]) => ({ fieldName: key, fieldValue: value })) : [];
      setFormData({
        title: threatContainer?.title,
        description: threatContainer?.description,
        severity: threatContainer?.severity,
        score: threatContainer?.score,
        impact: threatContainer?.impact,
        remediation: threatContainer?.remediation,
        ThreatActor: threatContainer?.data?.ThreatActor,
        Tactics: threatContainer?.data?.Tactics,
        ThreatActorDescription: threatContainer?.data?.ThreatActorDescription
      });
      setDynamicFields(dynamicFieldsData);
      setEvidence(threatContainer?.evidence)
    }
  }, [threatContainer]);

  // useEffect hook sets the main dimension label
  useEffect(() => {
    if (threatContainer && threatContainer.dimension && dimensionsContainer) {
      const mainDimension = dimensionsContainer
        .filter((dim) => !!dim.subDimensions)
        .find((dim) => dim.subDimensions.find((sub) => sub.id === threatContainer.dimension.id));

      if (mainDimension) {
        setMainDimensionLabel(mainDimension.label);
        setSubDimensionLabel(threatContainer.dimension.label)
        setSubDimensions(mainDimension.subDimensions || [])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensionsContainer, threatContainer]);

  // Handles changes in sub-dimension dropdown
  const handleSubDimensionChange = (event) => {
    const selectedSubDimensionObj = subDimensions?.find((subDim) => subDim.label === event.target.value);
    setSelectedSubDimension(selectedSubDimensionObj);
  };

  const determineValidSeverities = (score) => {
    const numericScore = Number(score)
    if (score === "") return ["Informational", "Low", "Medium", "High", "Critical"]
    if (numericScore === 0) return ["Informational"]
    if (numericScore >= 1 && numericScore <= 4) return ["Low"];
    if (numericScore > 4 && numericScore <= 6) return ["Medium"];
    if (numericScore > 6 && numericScore <= 8) return ["High"];
    if (numericScore > 8 && numericScore <= 10) return ["Critical"];
    if (numericScore > 10 || numericScore < 0) return [];
    return []
  };

  // Determines valid scores based on change in severities
  const determineValidScores = (severity) => {
    switch (severity) {
      case "Informational": return 0;
      case "Low": return 3;
      case "Medium": return 5;
      case "High": return 7;
      case "Critical": return 9;
      default: return 0;
    }
  };

  // Handles form validation errors
  const handleErrors = (fieldName) => {
    setFormErrors((prevformErrors) => ({
      ...prevformErrors,
      [fieldName]: true,
    }));
  };

  // Handles changes in additional fields
  const handleAdditionalFieldChange = (e) => {
    const { name, value } = e.target;
    if (name === "score") {
      const scoreValue = parseInt(value);
      if (isNaN(scoreValue) || scoreValue < 0 || scoreValue > 10) {
        setScoreError("Score must be a number between 0 and 10");
        const validSeverities = determineValidSeverities(value);
        setValidSeverities(validSeverities);
      } else {
        setScoreError("");
        const validSeverities = determineValidSeverities(value);
        setValidSeverities(validSeverities);
        if (!validSeverities.includes(formData.severity))
          setFormData({ ...formData, severity: '' });
      }
    }
    if (name === "severity") {
      const validScores = determineValidScores(value);
      setFormData({ ...formData, score: validScores, severity: value });
    }
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handles form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const updatedFormData = { ...formData };
    updatedFormData.score = determineValidScores(updatedFormData.severity)
    updatedFormData.dimension = selectedSubDimension?.id;
    updatedFormData.evidence = evidence;
    if (!checked)
      updatedFormData.title = updatedFormData.title.split(" ").map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ')
    const dynamicFieldData = dynamicFields.reduce((acc, field) => {
      if (field.fieldName === "ThreatActor" || field.fieldName === "Tactics") {
        acc[field.fieldName] = field.fieldValue
          .replace(/,/g, "\n")         // Replace commas with newlines
          .replace(/\n\s+/g, "\n");    // Remove whitespace after each newline
      } else {
        acc[field.fieldName] = field.fieldValue;
      }
      return acc;
    }, {});
    if(updatedFormData["ThreatActor"] ||  updatedFormData["Tactics"] || updatedFormData["ThreatActorDescription"]){
      dynamicFieldData.ThreatActor = updatedFormData["ThreatActor"];
      dynamicFieldData.Tactics = updatedFormData["Tactics"];
      dynamicFieldData.ThreatActorDescription = updatedFormData["ThreatActorDescription"]
  }
    const submissionData = {
      ...updatedFormData,
      ...dynamicFields.reduce((acc, field) => {
        acc[field.fieldName] = field.fieldValue;
        return acc;
      }, {})
    };
    delete updatedFormData.Tactics;
    delete updatedFormData.ThreatActor;
    delete updatedFormData.ThreatActorDescription;
    const payload = {
      ...updatedFormData,
      data: dynamicFieldData,
    };
    let payloadSize = new TextEncoder().encode(JSON.stringify(payload)).length;
    if (payloadSize > 5242880) {
      openNotification("Size is too large")
      return;
    }
    const emptyFields = Object.entries(submissionData)
      .filter(([name, value]) => value === '')
      .map(([name]) => name);

    if (emptyFields.length > 0) {
      const fieldNames = emptyFields.join(', ');
      openNotification(`Please fill in the required fields: ${fieldNames}`, 'error');
      return;
    }

    const response = await editVulnerabilityService(payload, id);
    if (response === "success") {
      openNotification('Vulnerability edited successfully', 'success');
      window.history.back();
    } else {
      openNotification('Failed to edit vulnerability:', 'error');
    }
  };

  // Handles adding a new dynamic field
  const handleAddField = () => {
    setDynamicFields([...dynamicFields, { fieldName: '', fieldValue: '' }]);
  };

  // Handles changing a dynamic field
  const handleDynamicFieldChange = (index, fieldKey, value) => {
    const updatedFields = [...dynamicFields];
    updatedFields[index][fieldKey] = value;
    setDynamicFields(updatedFields);
  };

  // Handles removing a dynamic field
  const handleRemoveField = (index) => {
    const updatedFields = [...dynamicFields];
    updatedFields.splice(index, 1);
    setDynamicFields(updatedFields);
  };

  const decodeHtmlEntities = (htmlString) => {
    if (!htmlString) return '';
    const decodedString = he.decode(htmlString);
    const style = 'style="max-width: 100%; height: auto;"';
    return decodedString.replace(/<img /g, `<img ${style} `);
  };

  return (
    <div>
      <Card className="card">
        <CardHeader
          title={
            <React.Fragment>
              <div className="card-head">
                <div className="flex-head">
                  <label className="label">
                    <UploadIcon className="upload-icon" />
                    Edit Vulnerability
                  </label>
                </div>
                <div>
                  <Button variant="contained" className="back font" onClick={() => window.history.back()}>
                    Back
                  </Button>
                </div>
              </div>
            </React.Fragment>
          }
        />
        <Divider className="divider" />
        <CardContent className="center-card">
          <Card className="user-card row company-screen-bg">
            <div className="row col-lg-12">
              <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md">
                <FormControl fullWidth>
                  <TextField
                    label="Dimension"
                    value={mainDimensionLabel}
                    disabled
                  />
                </FormControl>
              </div>
              <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md">
                <FormControl fullWidth>
                  <InputLabel htmlFor="subDimension">Sub-Dimension</InputLabel>
                  <Select
                    value={selectedSubDimension ? selectedSubDimension.label : subDimensionLabel}
                    onChange={handleSubDimensionChange}
                    label="Sub-Dimension"
                  >
                    {subDimensions.map((subDimension) => (
                      <MenuItem key={subDimension.id} value={subDimension.label}>
                        {subDimension.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md">
                <FormControl fullWidth>
                  <TextField
                    name="title"
                    label="Title"
                    value={formData.title}
                    onChange={handleAdditionalFieldChange}
                    onBlur={() => handleErrors('title')}
                    error={formData.title === '' && formErrors.title}
                    helperText={formData.title === '' && formErrors.title ? 'Field is required' : ''}
                  />
                  <FormControlLabel
                    className='mx-1'
                    control={<Checkbox checked={checked} onChange={() => setChecked(!checked)} />}
                    label="Preserve Title Format"
                  />
                </FormControl>
              </div>
              <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md">
                <FormControl fullWidth>
                  <TextField
                    multiline
                    maxRows={4}
                    name="description"
                    label="Description"
                    value={formData.description}
                    onChange={handleAdditionalFieldChange}
                    onBlur={() => handleErrors('description')}
                    error={formData.description === '' && formErrors.description}
                    helperText={formData.description === '' && formErrors.description ? 'Field is required' : ''}
                  />
                </FormControl>
              </div>
              <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md">
                <FormControl fullWidth>
                  <InputLabel htmlFor="severity">Severity</InputLabel>
                  <Select
                    name="severity"
                    value={formData.severity || validSeverities[0]}
                    onChange={handleAdditionalFieldChange}
                    label="Severity"
                  >
                    {validSeverities.map((severity, index) => (
                      <MenuItem key={index} value={severity}>
                        {severity}
                      </MenuItem>
                    ))}
                  </Select>
                  {formData.severity === '' && formErrors.severity && (
                    <FormHelperText>Field is required</FormHelperText>
                  )}
                </FormControl>
              </div>
              <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md">
                <FormControl fullWidth>
                  <TextField
                    name="score"
                    label="Score"
                    value={determineValidScores(formData.severity)}
                    onChange={handleAdditionalFieldChange}
                    onBlur={() => handleErrors('score')}
                    error={(formData.score === '' && formErrors.score) || !!scoreError}
                    helperText={(formData.score === '' && formErrors.score) ? 'Field is required' : scoreError || ''}
                    disabled
                  />
                </FormControl>
              </div>
              <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 pa-md">
                <FormControl fullWidth>
                  <TextField
                    multiline
                    maxRows={4}
                    name="impact"
                    label="Impact"
                    value={formData.impact}
                    onChange={handleAdditionalFieldChange}
                    onBlur={() => handleErrors('impact')}
                    error={formData.impact === '' && formErrors.impact}
                    helperText={formData.impact === '' && formErrors.impact ? 'Field is required' : ''}
                  />
                </FormControl>
              </div>
              <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 pa-md">
                <FormControl fullWidth>
                  <TextField
                    multiline
                    maxRows={4}
                    name="remediation"
                    label="Remediation"
                    value={formData.remediation}
                    onChange={handleAdditionalFieldChange}
                    onBlur={() => handleErrors('remediation')}
                    error={formData.remediation === '' && formErrors.remediation}
                    helperText={formData.remediation === '' && formErrors.remediation ? 'Field is required' : ''}
                  />
                </FormControl>
              </div>

              <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md">
                <FormControl fullWidth>
                  <TextField
                    multiline
                    name="ThreatActor"
                    label="ThreatActor"
                    maxRows={4}
                    value={formData.ThreatActor}
                    onChange={handleAdditionalFieldChange}
                    onBlur={() => handleErrors('threatactor')}
                    error={(formData.ThreatActor === '' && formErrors.ThreatActor)}
                    helperText={(formData.ThreatActor === '' && formErrors.ThreatActor) ? 'Field is required' : ''}
                  />
                </FormControl>
              </div>



              <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md">
                <FormControl fullWidth>
                  <TextField
                    multiline
                    name="Tactics"
                    label="Tactics"
                    maxRows={4}
                    value={formData.Tactics}
                    onChange={handleAdditionalFieldChange}
                    onBlur={() => handleErrors('Tactics')}
                    error={(formData.Tactics === '' && formErrors.Tactics)}
                    helperText={(formData.Tactics === '' && formErrors.Tactics) ? 'Field is required' : ''}
                  />
                </FormControl>
              </div>


              <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 pa-md">
                <FormControl fullWidth>
                  <TextField
                    multiline
                    name="ThreatActorDescription"
                    label="ThreatActorDescription"
                    maxRows={4}
                    value={formData.ThreatActorDescription}
                    onChange={handleAdditionalFieldChange}
                    onBlur={() => handleErrors('ThreatActorDescription')}
                    error={(formData.ThreatActorDescription === '' && formErrors.ThreatActorDescription)}
                    helperText={(formData.ThreatActorDescription === '' && formErrors.ThreatActorDescription) ? 'Field is required' : ''}
                  />
                </FormControl>
              </div>

              <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 pa-md">
                <Editor initialValue={decodeHtmlEntities(threatContainer?.evidence)} onChange={setEvidence} />
              </div>
              <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 pa-md">
                <Button variant="contained" onClick={handleAddField}>
                  Add More Fields
                </Button>
                <div className="note-content">
                  <p>
                    <strong>Note:</strong> You can use fields like <em>Threat Actors</em> as <code>ThreatActor</code>,
                    <em> Tactics</em> as <code>Tactics</code>, and <em>Threat Actor Description</em> as
                    <code>ThreatActorDescription</code>.
                  </p>
                </div>
              </div>
              {dynamicFields.map((field, index) => (
                <div className="row" key={index}>
                  <div className="col-lg-5 col-md-5 col-sm-12 col-xs-12 pa-md ">
                    <TextField
                      fullWidth
                      label="Field Name"
                      name="fieldName"
                      value={field.fieldName}
                      onChange={(e) => handleDynamicFieldChange(index, 'fieldName', e.target.value)}
                    />
                  </div>
                  <div className="col-lg-6 col-md-6 col-sm-12 col-xs-12 pa-md">
                    {["ThreatActor", "Tactics"].includes(field.fieldName) ? (<TextField
                      fullWidth
                      label="Field Value"
                      name="fieldValue"
                      value={field.fieldValue.replace(/\n\s*/g, ',')}
                      onChange={(e) => handleDynamicFieldChange(index, 'fieldValue', e.target.value.replace(/\n\s*/g, ','))}
                    />) :
                      (<TextField
                        fullWidth
                        label="Field Value"
                        name="fieldValue"
                        value={field.fieldValue}
                        onChange={(e) => handleDynamicFieldChange(index, 'fieldValue', e.target.value)}
                      />)
                    }
                  </div>
                  <div className="col-lg-1 col-md-1 col-sm-6 col-xs-6 pa-md">
                    <IconButton onClick={() => handleRemoveField(index)}>
                      <DeleteIcon sx={{ color: 'red' }} />
                    </IconButton>
                  </div>
                </div>
              ))}
              <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12 pa-md" style={{ textAlign: 'center' }}>
                <Button variant="contained" color="primary" onClick={handleSubmit}>
                  Submit
                </Button>
              </div>
            </div>
          </Card>
        </CardContent>
      </Card>
    </div>
  );
}

export default EditVulnerability;